/**
 * 業務フローの型・入口・出口を、画面に出す文にする。
 *
 * 読取は入口・出口を「種類 ＋ 参照」で返す（`FlowEntryView` / `FlowExitView`）。種類ごとに使う
 * 参照が違うので、文の組み立てを 1 か所に集める。見出し・右パネル・ステージの図の札・図の
 * 出口の札が同じ文を出す。
 *
 * **名前が空の参照は「未設定」と出す**（`displayLabel` と同じ扱い）。空のまま埋めると「出口:  が」の
 * ような文が出て、どこが欠けたのかが読めない。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務フローの型・入口・出口を、種類ごとに参照を当てた 1 行の文として組み立てる。
 * @graph-connects none
 */
import type {
  FlowEndingRowView,
  FlowEntryView,
  FlowExitView,
  FlowRefView,
  FlowView,
} from '@cortex/domain-spec-console';

/** i18next の `t` のうち、ここで使う形。 @graph-connects none */
export type FlowTextTranslate = (key: string, options?: Record<string, unknown>) => string;

/**
 * 参照 1 件の名前。無い・空なら「未設定」。
 *
 * @param ref 参照
 * @param t 訳
 * @returns 名前
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 入口・出口の参照の名前を、無いか空なら未設定として読む。
 * @graph-connects none
 */
export function refLabelOf(ref: FlowRefView | null, t: FlowTextTranslate): string {
  return ref === null || ref.label === '' ? t('spec.unset') : ref.label;
}

/**
 * 業務フローの型の語（メイン / サブ）。札ではなく文の中に出す形である。サブフローの役割は語に
 * しない（札と同じく、何の情報か読めない。Dhik の指示 2026-09-28）。
 *
 * @param type 型
 * @param t 訳
 * @returns 型の語
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務フローの型を、メインかサブの画面の語にする。
 * @graph-connects none
 */
export function flowTypeText(type: FlowView['type'], t: FlowTextTranslate): string {
  return t(type === 'main' ? 'flow.type.main' : 'flow.type.sub');
}

/**
 * 結末の行 1 件（業務が「結末」）の文。
 *
 * @param row 結末の行
 * @param t 訳
 * @returns 文
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 結末の行（業務と結果の値）を 1 行の文にする。
 * @graph-connects none
 */
export function endingRowText(row: FlowEndingRowView, t: FlowTextTranslate): string {
  return t('flow.endingRow', {
    business: refLabelOf(row.business, t),
    ending: refLabelOf(row.ending, t),
  });
}

/**
 * フローの入口の文。
 *
 * @param entry 入口
 * @param t 訳
 * @returns 入口の文
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務フローの入口（前のステージの出口・鎖の最初・時刻・操作・主フローの結末・外部の出来事）を 1 行の文にする。
 * @graph-connects refLabelOf [calls] 参照の名前
 */
export function flowEntryText(entry: FlowEntryView, t: FlowTextTranslate): string {
  switch (entry.kind) {
    case 'streamStart':
      return t('flow.entry.streamStart');
    case 'previousStage':
    case 'ending':
      return t(`flow.entry.${entry.kind}`, {
        business: refLabelOf(entry.business, t),
        ending: refLabelOf(entry.ending, t),
      });
    case 'timer':
    case 'external':
      return t(`flow.entry.${entry.kind}`, { term: refLabelOf(entry.term, t) });
    case 'operation':
      return t('flow.entry.operation', { actor: refLabelOf(entry.actor, t) });
  }
}

/**
 * フローの出口の行き先だけの文（図の札に「出口 → 行き先」として出す部分）。
 *
 * **ステージの出口は次のステージを行き先にする。** 読取が鎖から導いた次のステージ（`nextStages`）
 * が在ればその名前、無ければ（鎖の最後・まだ繋がっていない）出口の結末を出す。
 *
 * @param exit 出口
 * @param t 訳
 * @returns 行き先の文
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務フローの出口の行き先（次のステージか出口の結末・渡す先・相手・中止・戻すステージ）を短い文にする。
 * @graph-connects refLabelOf [calls] 参照の名前
 */
export function flowExitTargetText(exit: FlowExitView, t: FlowTextTranslate): string {
  switch (exit.kind) {
    case 'stageExit':
      return exit.nextStages.length === 0
        ? t('flow.exitTarget.stageExit', { ending: refLabelOf(exit.ending, t) })
        : t('flow.exitTarget.nextStage', {
            stages: exit.nextStages
              .map((stage) => refLabelOf(stage, t))
              .join(t('flow.listSeparator')),
          });
    case 'handoff':
      return refLabelOf(exit.business, t);
    case 'effect':
      return t('flow.exitTarget.effect', {
        effect: exit.effect === null ? t('spec.unset') : t(`flow.effect.${exit.effect}`),
        counterpart: refLabelOf(exit.counterpart, t),
      });
    case 'abort':
      return t('flow.exitTarget.abort');
    case 'previousStage':
      return refLabelOf(exit.stage, t);
  }
}

/**
 * フローの出口の文（種類の語 ＋ 行き先）。
 *
 * @param exit 出口
 * @param t 訳
 * @returns 出口の文
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務フローの出口を、種類の語と行き先で 1 行の文にする。
 * @graph-connects flowExitTargetText [calls] 行き先の文
 */
export function flowExitText(exit: FlowExitView, t: FlowTextTranslate): string {
  if (exit.kind === 'stageExit') {
    return t('flow.exit.stageExit', {
      business: refLabelOf(exit.business, t),
      ending: refLabelOf(exit.ending, t),
    });
  }
  return t(`flow.exit.${exit.kind}`, { target: flowExitTargetText(exit, t) });
}
